'use client';

import { useRef, useEffect, useState } from 'react';
import { gsap } from '@/lib/gsap';
import { PortfolioGrid } from '@/components/portfolio/PortfolioGrid';
import { projects } from '@/lib/data';

export function PortfolioPageClient() {
  const [activeCategory, setActiveCategory] = useState('All');
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLDivElement>(null);
  const portfolioGridRef = useRef<HTMLElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      // Hero is rendered visible by default for LCP; animate from a near-visible state.
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters && !prefersReducedMotion) {
        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        shuffledLetters.forEach((letter, index) => {
          gsap.fromTo(
            letter,
            { scaleY: 0.5 },
            { duration: 0.5, scaleY: 1, ease: 'power3.out', delay: index * 0.04 },
          );
        });
        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1000);
      } else if (letters) {
        applyHoverEffect(letters);
      }

      if (subtitleRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          subtitleRef.current,
          { y: 16, opacity: 0.4 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        );
      }
    });

    return () => ctx.revert();
  }, []);


  const title = 'PROJECTS';
  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter(p => p.category === activeCategory);

  return (
    <div className="relative min-h-screen">
      {/* Hero Section — white bg with black text, matching dilacode.com */}
      <section className="light-section relative z-10 flex min-h-[100dvh] w-full flex-col items-center justify-center overflow-hidden bg-white px-4 md:px-12 lg:px-16">

        {/* Subtitle text above title */}
        <div
          ref={subtitleRef}
          className="relative z-10 mb-6 max-w-3xl text-center"
        >
          <span className="eyebrow eyebrow--ink">
            Selected work — built in Erbil
          </span>
        </div>

        {/* Title */}
        <h1
          ref={titleRef}
          className="relative z-10 hero-title font-six-caps text-[134px] sm:text-[168px] md:text-[189px] lg:text-[calc(1rem+20vw)] leading-[0.75] text-black lowercase mix-blend-normal whitespace-nowrap"
          style={{ paddingBottom: '0.15em', opacity: 1 }}
        >
          {title.split('').map((char, i) => (
            <span
              key={i}
              className="letter inline-block origin-bottom text-black"
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Bottom row: Scroll to Explore (left) + Category filters (right) */}
        <div
          className="absolute left-0 right-0 z-10 w-full max-w-7xl mx-auto flex flex-col md:flex-row items-end justify-between"
          style={{ bottom: 'clamp(2rem, 5vw, 3.5rem)', paddingLeft: 'clamp(1.5rem, 4vw, 4rem)', paddingRight: 'clamp(1.5rem, 4vw, 4rem)', gap: '1.5rem' }}
        >

          <button
            onClick={() => portfolioGridRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center cursor-pointer order-2 md:order-1 transition-opacity duration-300 hover:opacity-70"
            style={{ gap: '0.75rem', background: 'none', border: 'none', padding: 0 }}
          >
            <span className="uppercase text-black/60" style={{ fontSize: '11px', letterSpacing: '0.2em' }}>Scroll to Explore</span>
            <svg
              className="text-black/60"
              style={{ width: '14px', height: '14px' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className="flex flex-wrap items-center order-1 md:order-2" style={{ gap: '0.5rem' }}>
            {['All', 'Mobile App', 'Web Design'].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="cursor-pointer transition-all duration-300"
                style={{
                  paddingLeft: '1.25rem',
                  paddingRight: '1.25rem',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderRadius: '9999px',
                  fontSize: '11px',
                  fontWeight: 500,
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.15em',
                  border: activeCategory === cat ? '1px solid rgb(0,0,0)' : '1px solid rgba(0,0,0,0.15)',
                  color: activeCategory === cat ? 'rgb(0,0,0)' : 'rgba(0,0,0,0.62)',
                }}
              >
                {cat}
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* Portfolio Showcase Grid — still on white, part of the light section */}
      <section ref={portfolioGridRef} className="light-section relative z-10 bg-white" style={{ paddingTop: '20px', paddingBottom: '80px' }}>
        <PortfolioGrid projects={filteredProjects} />
      </section>

    </div>
  );
}
